// @ts-check
/* global module $ Accept require grecaptcha */
'use_strict';

declare var $;
declare var grecaptcha;

import { Notifications } from '../tv_utils/notifications';

import {
    send_third_party_error_notification,
    resize_last_dialog
} from '../tv_utils/utils.js';

import { PayPal } from './paypal';

/**
 * Tab class to show and hide the different payment options.
 *
 * @property {HTMLCollectionOf<Element>} tabs elements containing the content to show and hide.
 * @property {HTMLCollectionOf<Element>} buttons elements containing the buttons to control the tabs.
 * @property {number} active_idx index of the tab that is active and visible.
 */
class PaymentTabs {

    tabs: HTMLCollectionOf<HTMLElement>;
    buttons: HTMLCollectionOf<HTMLElement>;
    active_idx: number = 0;
    active_name: string = '';
    callback: Function;

    /**
     * Accepts the html elements to bind the controls, content, and set the active tab.
     *
     * @param {HTMLCollectionOf<Element>} tabs from js-tab-content
     * @param {HTMLCollectionOf<Element>} buttons from js-tab-links
     * @param {number} active_idx which tab should be active and visible.
     * @param {Function} callback for when a tab is changed.
     */
    constructor(
        tabs: HTMLCollectionOf<HTMLElement>,
        buttons: HTMLCollectionOf<HTMLElement>,
        active_idx: number,
        callback: Function = null
    ) {
        this.tabs = tabs;
        this.buttons = buttons;
        this.callback = callback;

        if (this.buttons.length > 0) {
            this.bind();
            this.set_active(active_idx);
        }
    }

    /**
     * Binds the UI elements to event listeners.
     *
     * @private
     * @returns {void}
     */
    bind(): void {
        for (const button of this.buttons) {
            button.addEventListener('click', this.button_clicked.bind(this), false);
        }
    }

    /**
     * Handles the button clicked to change the active tab.
     *
     * @listens click
     * @param {Event} e from click event.
     * @returns {void}
     */
    button_clicked(e: Event): void {
        const caller = <HTMLElement>e.target;
        const idx = parseInt(caller.getAttribute('data-idx'), 10);

        if (idx !== this.active_idx) {
            this.set_active(idx);
        }
    }

    /**
     * Hides the active tab and shows the passed tab.
     *
     * @private
     * @param {number} idx of the tab to show.
     * @returns {void}
     */
    set_active(idx: number): void {
        // Update the UI styling.
        this.buttons[this.active_idx].classList.remove('active');
        this.buttons[idx].classList.add('active');
        this.active_name = this.buttons[idx].attributes.getNamedItem('rel').value;

        this.tabs[this.active_idx].style.display = 'none';
        // Any faster and this causes visual errors.
        setTimeout(() => {
            this.tabs[idx].style.display = 'block';

            this.active_idx = idx;

            if (this.callback) {
                this.callback();
            }
        }, 1);
    }

    /**
     * Disables tab by hiding the tab link.
     * 
     * @param {string} payment_method name of payment method ('cc', 'pay_later', 'paypal', etc).
     * @returns {void}
     */
    disable_tab(payment_method: string): void {
        for (const button of this.buttons) {
            if (button.attributes.getNamedItem('rel').value === payment_method) {
                button.classList.add('hidden');
            }
        }
    }
}

/**
 * Class for handing vertical payment tabs.
 * 
 * @property {Array<HTMLInputElement>} tab_radio_buttons Radio buttons for each payment tab.
 * @property {string} active_name name of the active tab.
 */
class VerticalPaymentTabs {

    tab_radio_buttons: Array<HTMLInputElement>;
    active_name: string = '';

    /**
     * Instantiates the VerticalPaymentTabs class.
     * 
     * @param callback To be called each time the tab is changed.
     */
    constructor(callback: Function = null) {
        this.tab_radio_buttons = Array.from(
            document.querySelectorAll('input[name="payment_info[type]"][type="radio"]')
        );

        for (const radio_button of this.tab_radio_buttons) {
            if (radio_button.checked) {
                this.active_name = radio_button.value;
            }

            radio_button.addEventListener('change', () => {
                this.active_name = radio_button.value;

                // Make sure the newly selected tab is in the viewport after the previous tab collapses.
                if (!this.tab_in_viewport(radio_button)) {
                    radio_button.parentElement.scrollIntoView();
                }

                if (callback) {
                    callback();
                }

            });
        }
    }

    /**
     * Determines whether the tab header is inside the viewport.
     *
     * @param tab_radio_button Tab button to check.
     * @returns true if tab is in viewport, false otherwise.
     */
    tab_in_viewport(tab_radio_button: HTMLInputElement): boolean {
        const rect = tab_radio_button.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    /**
     * Disables a payment tab by hiding it from the user.
     *
     * @param tab_value The value of the tab to hide ('cc_new', 'paypal', etc.).
     */
    disable_tab(tab_value: string) {
        const tab_to_disable = this.tab_radio_buttons.find(button => button.value === tab_value);
        if (tab_to_disable) {
            tab_to_disable.parentElement.style.display = 'none';
        }
    }

}

/**
 * Class to send to get credit card token using Accept.js.
 *
 * @property {Object<string, string>} auth_data to hold authorization credentials.
 * @property {Object<string, string>} card_data to hold card information.
 * @property {string} card_name is not included in card_data since it is not passed to Accept.js.
 * @property {boolean} card_data_set flag to tell whether card data has been set yet.
 * @property {Object<string, any>} response to hold data that is sent back in the response.handle_response.
 * @property {requestCallback} callback the handler function to be called when request is finished.
 * @property {bool} submitted flag to track if a request was made recently.
 */
class AcceptJSRequest {

    auth_data: { [key: string]: string };
    card_data: { [key: string]: string } = {
        cardNumber: null,
        month: null,
        year: null,
        cardCode: null
    };
    card_name: string = null;
    card_data_set: boolean = false;
    response: { [key: string]: any } = {
        error: false,
        error_msgs: [],
        data_descriptor: null,
        data_value: null
    };
    callback: Function = null;
    submitted: boolean = false;

    /**
     * @param {string} client_key for accepts.js authorization.
     * @param {string} api_login_id for accepts.js authorization.
     */
    constructor(client_key: string, api_login_id: string) {
        this.auth_data = {
            clientKey: client_key,
            apiLoginID: api_login_id
        };
    }

    /**
     * Function to clear the submitted flag in a setTimeout callback.
     * This is needed to work around the Accept.js issue where sometimes you get a false,
     * error callback right after a good one went through. By clearing the flag after a few ms,
     * we ensure the user can still change their card.
     *
     * @returns {void}
     */
    clear_submitted(): void {
        this.submitted = false;
    }

    /**
     * Sets card data from passed in values.
     *
     * @param {string} name for cc.
     * @param {string} number for cc.
     * @param {string} month expiration for cc.
     * @param {string} year expiration for cc.
     * @param {string} code cvv2 code for cc.
     * @return {void}
     */
    set_card_data(name: string, number: string, month: string, year: string, code: string): void {
        this.card_name = name;
        this.card_data.cardNumber = number;
        this.card_data.month = month;
        this.card_data.year = year;
        this.card_data.cardCode = code;

        this.card_data_set = true;
    }

    /**
     * Pulls card data values from add cc form.
     *
     * @return {void}
     */
    set_card_data_from_form(): void {
        const card_name = (<HTMLInputElement>document.getElementById('credit_card_name_1')).value;
        const month = (<HTMLInputElement>document.getElementById('credit_card_month_1')).value;
        const year = (<HTMLInputElement>document.getElementById('credit_card_year_1')).value;
        const card_code = (<HTMLInputElement>document.getElementById('credit_card_cvv2_1')).value;
        const card_number = (<HTMLInputElement>document.getElementById(
            'credit_card_number_1'
        )).value.replace(/\s+/g, '');

        this.set_card_data(card_name, card_number, month, year, card_code);
    }

    /**
     * Sends the new cc request to Accept.js
     *
     * @private
     * @param {Function} callback The handler function for Authorize.Net's dispatchData function.
     * @returns {void}
     */
    send_request(callback: Function): void {
        this.callback = callback;
        if (!this.card_data_set) {
            this.set_card_data_from_form();
        }

        this.response.error = false;
        if (this.card_name == null || this.card_name.trim() === '') {
            this.response.error_msgs.push('Please provide valid cardholder\'s name.');
            this.response.error = true;
        }
        if (this.card_data.cardNumber == null || this.card_data.cardNumber.trim() === '') {
            this.response.error_msgs.push('Please provide valid credit card number.');
            this.response.error = true;
        }
        if (this.card_data.year == null || this.card_data.year.trim() === '') {
            this.response.error_msgs.push('Please provide valid expiration year.');
            this.response.error = true;
        }
        if (this.card_data.month == null || this.card_data.month.trim() === '') {
            this.response.error_msgs.push('Please provide valid expiration month.');
            this.response.error = true;
        }
        if (this.card_data.cardCode == null || this.card_data.cardCode.trim() === '') {
            this.response.error_msgs.push('Please provide valid CVV.');
            this.response.error = true;
        }

        if (this.response.error) {
            this.callback(this.response);
            return;
        }

        // Check to see if the card number matches any invalid card numbers we have in the config.
        // If it does return the response with an error.
        // @ts-ignore "'js_config' does not exist".
        const invalid_card_numbers = window.js_config.invalid_cc_numbers;
        if (invalid_card_numbers && invalid_card_numbers.includes(this.card_data.cardNumber)) {
            this.response.error = true;
            this.response.error_msgs.push('Invalid card number.');
            this.callback(this.response);
            return;
        }

        const secureData = {
            authData: this.auth_data,
            cardData: this.card_data
        };

        // @ts-ignore "Cannot find name 'Accept'".
        Accept.dispatchData(secureData, this.handle_response.bind(this));
    }

    /**
     * Callback to handle a response from Accept.js
     *
     * @public
     * @param {object} response data from Accept.js
     * @returns {void}
     */
    handle_response(response): void {
        // Don't run if we already have a good response.
        if (this.submitted === true) {
            return;
        }

        if (response.messages.resultCode === 'Ok') {
            this.response.data_descriptor = response.opaqueData.dataDescriptor;
            this.response.data_value = response.opaqueData.dataValue;

            // Note that we got a valid response.
            this.submitted = true;
            // Set a timer to clear the flag, incase they submit another card.
            setTimeout(this.clear_submitted.bind(this), 200);
        } else {
            this.response.error = true;
            // These are the error codes in which we will allow the client to see directly from Accept.js.
            const display_codes = {
                // Invalid number.
                'E_WC_05': true,
                // Invalid month.
                'E_WC_06': true,
                // Invalid year.
                'E_WC_07': true,
                // Date not in future.
                'E_WC_08': true,
                // Invalid CCV.
                'E_WC_15': true,
                // Invalid Name.
                'E_WC_17': true,
                // Please try again.
                'E_WC_19': true
            };
            const backend_error_msgs = ['Accepts.js errors not displayed to users:'];

            // Collect the error messages from the response to be displayed to the customer, but only if they are
            // in the list above.  If they are not we want to collect them to be passed to any third party error
            // reporting services we are using.
            if (response.hasOwnProperty('messages')) {
                for (const message of response.messages.message) {
                    if (display_codes.hasOwnProperty(message.code) && display_codes[message.code] === true) {
                        this.response.error_msgs.push(message.text);
                    } else {
                        backend_error_msgs.push(message.text);
                    }
                }
            }

            // If there were no codes to display, use this generic one.
            if (this.response.error_msgs.length < 1) {
                this.response.error_msgs.push(
                    'There was a problem validating the card. Please check the card information and try again.'
                );

                // Log any errors we didn't display to the customer.
                send_third_party_error_notification(backend_error_msgs.join(' '));
            }
        }

        this.callback(this.response);
    }
}

/**
 * Class to handle the UI interaction with the payment method selection,
 * including interaction with Accept.js for new credit cards.
 *
 * @property {Object<string, Element>} elements on the page that will be interacted with.
 * @property {boolean} is_valid gets set to false when a payment method isn't valid or to true when it is.
 * @property {string} payment_type the type of the payment such as a new or saved credit card, net terms or paying later.
 * @property {Function} selected_callback called when a payment is selected.
 * @property {Function} changed_callback called when the selected payment's change button is clicked.
 */
class PaymentSelector {

    options: { [key: string]: boolean };
    elements: { [key: string]: any } = {};
    is_valid: boolean = false;
    payment_type: string;
    selected_callback: Function;
    changed_callback: Function;
    accepted_payment_options: { [key: string]: string };

    /**
     * Creates tabs, binds to the elements, and controls, and sets the UI state.
     *
     * @param {Object<string, boolean>} options used to enable or disable functionality for the four payment types.
     * @param {Function} selected_callback for when a payment method is selected.
     * @param {Function} changed_callback for when the selected method is changed.
     */
    constructor(
        options: { [key: string]: boolean },
        selected_callback: Function = null,
        changed_callback: Function = null
    ) {
        this.options = options;
        this.payment_type = 'cc_new';
        this.selected_callback = selected_callback;
        this.changed_callback = changed_callback;
        this.bind();
        // Hide the new cc form if the payment is being auto selected.
        // This is done in JS instead of Smarty because in our TPL hook we don't have access to the full document body.
        if (this.elements.auto_selected_payment.value === '1') {
            this.toggle_new_cc_form(false);
        }
    }

    /**
     * Binds to our form elements, and creates event listeners.
     *
     * @private
     * @returns {void}
     */
    bind(): void {
        // Create references to elements used more than once.
        this.elements = {
            'auto_selected_payment': document.getElementById('js-auto-selected-payment'),
            'saved_ccs': document.getElementById('js-saved-ccs'),
            'credit_card_number': document.getElementById('credit_card_number_1'),
            'credit_card_month': document.getElementById('credit_card_month_1'),
            'credit_card_year': document.getElementById('credit_card_year_1'),
            'credit_card_cvv2': document.getElementById('credit_card_cvv2_1'),
            'credit_card_name' : document.getElementById('credit_card_name_1'),
            'payment_methods': document.getElementById('js-payment-methods'),
            'selected_payment_method': document.getElementById('js-selected-payment-method'),
            'selected_cc': document.getElementById('js-selected-cc'),
            'selected_net_terms': document.getElementById('js-selected-net-terms'),
            'selected_pay_later': document.getElementById('js-selected-pay-later'),
            'selected_paypal': document.getElementById('js-selected-paypal'),
            'new_cc_form': document.getElementById('js-new-cc-form'),
            'last_four': document.getElementById('js-payments-last-four'),
            'cc_icons_items': document.getElementsByClassName('ty-cc-icons__item'),
            'set_preferred_cc_checkbox_container': document.getElementById('js-credit-card-preferred-container'),
            'cc_input': document.getElementById('js-cc-payment')
        };

        // A dictionary of the accepted payment methods.
        this.accepted_payment_options = {
            'visa': 'Visa',
            'mastercard': 'MasterCard',
            'american-express': 'American Express',
            'discover': 'Discover'
        };

        // Add events to referenced elements.
        if (this.elements.saved_ccs !== null) {
            if (this.elements.saved_ccs.value) {
                this.payment_type = 'cc_saved';
            }
            this.elements.saved_ccs.addEventListener('change', () => {
                if (this.elements.cc_input) {
                    this.elements.cc_input.value = 'cc_saved'
                }
                this.select_payment('cc_saved');
            });
        }

        // One time bindings.
        this.elements.add_cc_button = document.getElementById('js-select-payment-button');
        if (this.elements.add_cc_button) {
            this.elements.add_cc_button.addEventListener('click', this.handle_card.bind(this));
        }

        if (this.options.pay_later) {
            if (document.getElementById('js-select-pay-later')) {
                document.getElementById('js-select-pay-later').addEventListener('click',
                    this.select_payment.bind(this, 'pay_later')
                );
            }
        }

        if (this.options.net_terms) {
            if (document.getElementById('js-select-net-terms')) {
                document.getElementById('js-select-net-terms').addEventListener('click',
                    this.select_payment.bind(this, 'net_terms')
                );
            }
        }

        if (this.options.paypal) {
            const select_paypal_btn = document.getElementById('js-select-paypal');
            if (select_paypal_btn) {
                select_paypal_btn.addEventListener('click', this.select_payment.bind(this, 'paypal'));
            }
        }

        const selectedPayment = document.getElementById('js-change-selected-payment');
        if (selectedPayment) {
            selectedPayment.addEventListener('click', this.reset.bind(this));
        }

        // Check to see if there are saved credit cards. If so, display the set preferred checkbox.
        if (this.elements.saved_ccs) {
            this.elements.set_preferred_cc_checkbox_container.classList.remove('hidden');
        }

        // Event listeners to handle credit card month and name validation.
        this.elements.credit_card_month.addEventListener('blur', this.validate_credit_card_month);
        this.elements.credit_card_name.addEventListener('blur', this.validate_credit_card_name);

    }

    /**
     * Handles the validation of month for the credit and adds 0 to single digit input;
     *
     * @param {Event} e The event element.
     * @returns {void}
     */
    validate_credit_card_month(e: Event): void {
        const cc_month = <HTMLInputElement>e.target;
        if (cc_month.value.trim().length === 1 && parseInt(cc_month.value, 10) >= 1) {
            cc_month.value = '0' + cc_month.value;
        }

        cc_month.setCustomValidity('');
        if (parseInt(cc_month.value, 10) > 12 || parseInt(cc_month.value, 10) === 0) {
            cc_month.setCustomValidity('Please Input Month 1-12');
            cc_month.reportValidity();
        }

    }

    /**
     * Handles the validation of name for the credit card.
     * 
     * @param {Event} e The event element.
     * @returns {void}
     */
    validate_credit_card_name(e: Event): void {
        const cc_name = <HTMLInputElement>e.target;
        const trimmed_name = cc_name.value.trim();
        cc_name.value = trimmed_name;
    }

    /**
     * Handles the validation of the selected credit card.
     *
     * @returns {void}
     */
    handle_card(): void {
        this.make_request(this.select_card.bind(this));
    }

    /**
     * Initialize AcceptJs request class, set the card data, and finally send the request to authorize.
     *
     * @param {Function} callback The function to call after the accept.js request is finished.
     * @returns {void}
     */
    make_request(callback: Function): void {
        const acceptjs_request = new AcceptJSRequest(
            (<HTMLInputElement>document.getElementById('js-accept-js-client-key')).value,
            (<HTMLInputElement>document.getElementById('js-accept-js-login-id')).value
        );
        acceptjs_request.set_card_data(
            this.elements.credit_card_name.value,
            this.elements.credit_card_number.value.replace(/\s+/g, ''),
            this.elements.credit_card_month.value,
            this.elements.credit_card_year.value,
            this.elements.credit_card_cvv2.value
        );
        acceptjs_request.send_request(callback);
    }

    /**
     * Handles the selection of a new card.
     *
     * @param {object} response The response from Authorize.Net
     * @returns {void}
     */
    select_card(response: {}): void {
        this.handle_acceptjs_response(response);
    }

    /**
     * Callback to handle a response from Accept.js
     *
     * @public
     * @param {object} response data from Accept.js
     * @returns {void}
     */
    handle_acceptjs_response(response): void {
        if (!response.error) {
            this.is_valid = true;
            (<HTMLInputElement>document.getElementById('js-accept-js-data-descriptor')).value = response.data_descriptor;
            (<HTMLInputElement>document.getElementById('js-accept-js-data-value')).value = response.data_value;
            this.select_payment('cc_new');
        } else {
            Notifications.set_notification(
                'E',
                'Failed to authorize card.',
                response.error_msgs.join(' ')
            );
            this.is_valid = false;
        }
    }

    /**
     * Deselects any selected payments, and returns the form to its initial state without auto select.
     *
     * @private
     * @returns {void}
     */
    reset(): void {
        this.elements.payment_methods.style.display = 'block';

        this.elements.selected_payment_method.style.display = 'none';
        this.elements.selected_cc.style.display = 'none';

        if (this.elements.cc_input) {
            this.elements.cc_input.value = 'cc_new'
        }

        if (this.elements.selected_net_terms) {
            this.elements.selected_net_terms.style.display = 'none';
        }

        if (this.elements.selected_pay_later) {
            this.elements.selected_pay_later.style.display = 'none';
        }

        if (this.elements.selected_paypal) {
            this.elements.selected_paypal.style.display = 'none';
        }

        this.elements.auto_selected_payment.value = false;

        if (this.elements.saved_ccs) {
            this.elements.saved_ccs.selectedIndex = 0;
        }

        this.toggle_new_cc_form(true);

        const next_button_container = document.querySelector('.js-next-button-container');

        if (next_button_container) {
            next_button_container.classList.add('hidden');
        }

        if (this.changed_callback) {
            this.changed_callback();
        }
    }

    /**
     * Show the selected payment, and hides UI elements to select one.
     *
     * @param {string} type which type of payment was selected.
     * @param {boolean} display_summary whether to display the selection summary.
     * @returns {void}
     */
    select_payment(type: string, display_summary: boolean = true): void {
        this.payment_type = type;

        if (display_summary) {
            this.display_selection_summary();
        }

        if (this.selected_callback) {
            this.selected_callback();
        }
    }

    /**
     * Hides payment method inputs and displays summary of selected input method.
     */
    display_selection_summary() {
        this.elements.payment_methods.style.display = 'none';
        this.elements.selected_payment_method.style.display = 'inline';

        if (this.payment_type === 'cc_new') {
            const last_four = this.elements.credit_card_number.value.replace(/\s+/g, '').slice(-4);
            const exp_date = this.elements.credit_card_month.value + '/' + this.elements.credit_card_year.value;

            // Set the text for the selected payment method by parsing through
            // the CSS classes to pull out the payment method.
            for (const cc_icon of this.elements.cc_icons_items) {
                if (cc_icon.classList.contains('active')) {
                    // Once the element has been found, find the span element as it contains the payment method class.
                    const span = cc_icon.querySelector('span');
                    // The payment method class is the second class in the class list.
                    const selected_cc = this.accepted_payment_options[span.classList[1]];
                    this.elements.selected_cc.innerText = `${selected_cc} ending in ${last_four} expires ${exp_date}.`;
                }
            }

            this.elements.selected_cc.style.display = 'inline';
            this.elements.last_four.value = last_four;
        } else if (this.payment_type === 'cc_saved') {
            const text = this.elements.saved_ccs.options[this.elements.saved_ccs.selectedIndex].text;

            this.elements.selected_cc.innerText = text;
            this.elements.selected_cc.style.display = 'inline';
        } else if (this.payment_type === 'net_terms') {
            this.elements.selected_net_terms.style.display = 'inline';
        } else if (this.payment_type === 'pay_later') {
            this.elements.selected_pay_later.style.display = 'inline';
        } else if (this.payment_type === 'paypal') {
            this.elements.selected_paypal.style.display = 'inline';
        }

        if (this.payment_type !== 'cc_new') {
            this.toggle_new_cc_form(false);
        }

        const next_button = document.querySelector('.js-next-button-container');
        if (next_button) {
            next_button.classList.remove('hidden');
        }

    }

    /**
     * Handles the new cc form's visibility and if it's part of the checkout form or not.
     *
     * When the form is not visible it is moved to the bottom of the document body and hidden
     * so that it does not interfere with cscart's native validation.
     *
     * When the form is made visible again, it is moved back.
     *
     * @param {boolean} show true to show, false to hide.
     * @returns {void}
     */
    toggle_new_cc_form(show: boolean): void {
        if (show) {
            this.payment_type = 'cc_new';
            // Be sure to move before showing.
            document.getElementById('js-cc-form-anchor').appendChild(this.elements.new_cc_form);
            this.elements.new_cc_form.style.display = 'block';
        } else {
            // Be sure to hide before moving.
            this.elements.new_cc_form.style.display = 'none';
            document.body.appendChild(this.elements.new_cc_form);
        }

        // Check to see if we are in a submit payment modal and if we are
        // we want to resize it to fit the content.
        const dialog = $.ceDialog('get_last')[0];
        if (dialog) {
            $.ceDialog('get_last').ceDialog('reload');
        }
    }
}

/**
 * Class to manage the UI for payment method selection during the checkout.
 *
 * @property {VerticalPaymentTabs} tabs to display the different payment options.
 * @property { [key: string]: HTMLElement } elements elements for checkout payment methods.
 * @property {PaymentSelector} payment_selector elements for the actual tab content.
 * @property {PayPal} paypal for interacting with the PayPal JS API.
 */
export class CheckoutView {

    tabs: VerticalPaymentTabs;
    elements: { [key: string]: HTMLElement };
    payment_selector: PaymentSelector;
    paypal: PayPal;

    /**
     * Instantiate and bind to the tabs and payment selection form.
     *
     * @param {Function} selected_callback for when a payment method is selected.
     * @param {Function} changed_callback for when the user chooses another option.
     * @param {Function} tabbed_callback for when the user changes the payment tab.
     * @param {Function} paypal_getter for getting current order totals.
     * @param {Function} paypal_callback for when the user approves a PayPal order.
     */
    constructor(
        selected_callback: Function,
        changed_callback: Function,
        tabbed_callback: Function,
        paypal_getter: Function,
        paypal_callback: Function
    ) {
        this.tabs = new VerticalPaymentTabs(() => {
            tabbed_callback();
            this.payment_selector.reset();
        });

        // Bind the selected payment label.
        this.elements = {
            selected_payment_label: document.querySelector('.tv-payments__selected-payment-label')
        };

        this.paypal = new PayPal(paypal_getter, paypal_callback);

        // Hide PayPal tab if PayPal is disabled.
        if (!this.paypal.enabled) {
            this.tabs.disable_tab('paypal');
        }

        // Use all four types during checkout.
        this.payment_selector = new PaymentSelector(
            {
                'cc_saved': true,
                'cc_new': true,
                'net_terms': true,
                'pay_later': true,
                'paypal': this.paypal.enabled
            },
            selected_callback,
            changed_callback
        );

    }
}

/**
 * Simplified view class to manage the basic shared UI for the making a payment.
 *
 * @property {PaymentSelector} payment_selector to handle the selection of new and saved credit cards.
 * @property {Object<string, HTMLInputElement>} elements references to all of the needed UI elements.
 * @property {string} result_ids of the div to replace with the receipt.
 * @property {boolean} allow_multiple_payments flag of whether multiple orders can be payed for.
 * @property {callback} callback to run after the receipt is displayed.
 * @property {boolean} logged_in flag of whether user is logged in.
 */
export class SimplePaymentView {

    payment_selector: PaymentSelector;
    result_ids: string;
    allow_multiple_payments: boolean;
    callback: Function;
    logged_in: boolean;
    elements: { [key: string]: HTMLInputElement } = {};
    tabs: VerticalPaymentTabs;
    paypal: PayPal;
    cc_pay_button: HTMLButtonElement;

    /**
     * Instantiate and bind to the form elements.
     *
     * @param {Object<string, boolean>} accepted_payment_types that we will accept for payment.
     * @param {string} result_ids of the div to replace with the receipt.
     * @param {boolean} allow_multiple_payments flag of whether multiple orders can be payed for.
     * @param {function} receipt_callback to run after the receipt is displayed.
     * @param {boolean} logged_in flag of whether user is logged in.
     */
    constructor(
        accepted_payment_types: { [key: string]: boolean },
        result_ids: string,
        allow_multiple_payments: boolean,
        receipt_callback: Function,
        logged_in: boolean
    ) {
        this.bind();
        this.payment_selector = new PaymentSelector(accepted_payment_types);
        this.paypal = new PayPal(
            () => this.elements.amount && {order_total: this.elements.amount.value},
            this.submit.bind(this),
            false
        );

        this.tabs = new VerticalPaymentTabs(this.select_tab_callback.bind(this));

        if (!this.paypal.enabled) {
            this.tabs.disable_tab('paypal');
        }

        this.result_ids = result_ids;
        this.allow_multiple_payments = allow_multiple_payments;
        this.callback = receipt_callback;
        this.logged_in = logged_in;

        // Hide payment validation message by default.
        const payment_ok_element = document.getElementById('js-submit-payment-reference-ok')
        if (payment_ok_element) {
            payment_ok_element.classList.add('tv-display__none');
        }
        const payment_error_element = document.getElementById('js-submit-payment-reference-err');
        if (payment_error_element) {
            payment_error_element.classList.add('tv-display__none');
        }
    }

    /**
     * Adds references to the UI elements and adds event listeners.
     *
     * @private
     * @returns {void}
     */
    bind(): void {
        /**
         * Shared elements between logged in and out.
         */
        this.elements.amount = <HTMLInputElement>document.getElementById('tv-submit-payment-amount');
        this.cc_pay_button = <HTMLButtonElement>document.getElementById('js-submit-payment-pay-btn');
        this.cc_pay_button.addEventListener('click', this.handle_card.bind(this));

        this.elements.selected_cc = <HTMLInputElement>document.getElementById('js-selected-cc');
    }

    /**
     * Callback for when a tab is selected. Used to update the payment type and render PayPal buttons.
     * 
     * @returns {void}
     */
    select_tab_callback(): void {
        if (this.tabs.active_name === 'paypal' && this.paypal.enabled) {
            this.payment_selector.select_payment('paypal', false);
            this.paypal.render_buttons();
            this.cc_pay_button.classList.add('hidden');
        } else if (this.tabs.active_name.startsWith('cc') && !this.payment_selector.payment_type.startsWith('cc')) {
            this.payment_selector.select_payment('cc_new', false);
            this.cc_pay_button.classList.remove('hidden');
        }
        resize_last_dialog();
    }

    /**
     * Handles the validation of the selected credit card.
     *
     * @param {Event} e from the submit button.
     * @returns {void}
     */
    handle_card(e: Event): void {
        // Stop HTML from posting the form.
        e.preventDefault();
        e.stopPropagation();
        // Only make the accept.js request if it is a new card.
        if (this.payment_selector.payment_type === 'cc_new') {
            this.payment_selector.make_request(this.submit.bind(this));
        } else {
            this.submit();
        }
    }

    /**
     * Post the form in hopes of processing the payment.
     *
     * @param {Object} response The response from Authorize.Net
     * @returns {void}
     */
    submit(response: {} = null): void {
        const type = this.payment_selector.payment_type;
        if (type === 'cc_new') {
            this.payment_selector.handle_acceptjs_response(response);
            if (!this.payment_selector.is_valid) {
                return;
            }
        }

        if (!this.logged_in) {
            // Validate the Google Recaptcha by grabbing our Custom CSS Class for its response.
            const recaptcha_response = <HTMLInputElement>document.querySelector('.g-recaptcha-response');
            // @ts-ignore "Cannot find name 'grecaptcha'".
            if (grecaptcha.getResponse().length === 0) {
                recaptcha_response.setCustomValidity('Recaptcha requires verification.');
                recaptcha_response.setAttribute('required', 'required');
                recaptcha_response.reportValidity();
                return;
            }
            recaptcha_response.removeAttribute('required');
            recaptcha_response.setCustomValidity('');
        }

        const form_data = $('#js-submit-payment-form').serializeArray();

        // CS Cart doesn't like the jQuery serialization so we have to reassign the objects to key value pairs.
        const data = {};
        for (const property of form_data) {
            data[property.name] = property.value;
        }

        if (!data.hasOwnProperty('amount')) {
            data['amount'] = (<HTMLInputElement>document.getElementById('tv-submit-payment-amount')).value;
        }

        data['selected_cc'] = this.elements.selected_cc.innerText;

        $.ceAjax('request', 'index.php?dispatch=tv_payments.pay', {
            'full_render': false,
            'method': 'POST',
            'caching': false,
            'data': data,
            'callback': this.display_receipt.bind(this)
        });

    }

    /**
     * Display a receipt when the payment was submitted.
     *
     * @param {Object} data CS Cart ajax response
     * @returns {void}
     */
    display_receipt(data): void {
        if (data.json.is_paid === true) {
            data.allow_multiple_payments = this.allow_multiple_payments;
            $.ceAjax('request', 'index.php?dispatch=tv_payments.receipt', {
                'full_render': false,
                'method': 'POST',
                'caching': false,
                'data': {
                    data: data,
                    result_ids: this.result_ids
                },
                'callback': this.receipt_callback.bind(this)
            });
        }
    }

    /**
     * Callback to handle passed in receipt callback if one was supplied.
     *
     * @callback
     * @returns {void}
     */
    receipt_callback(): void {
        if (this.callback) {
            this.callback();
        }
    }
}

/**
 * Class to manage the UI for the submit payment form.
 *
 * @property {SimplePaymentView} payment_view simplified view class to manage the basic shared UI.
 * @property {Object<string, HTMLElement>} elements references to all of the needed UI elements.
 * @property {boolean} logged_in flag for whether user is logged in or not.
 */
export class SubmitPaymentView {

    elements: { [key: string]: any } = {};
    logged_in: boolean = false;
    payment_view: SimplePaymentView;
    paypal: PayPal;

    /**
     * Instantiate and bind to the form elements.
     */
    constructor() {
        // Only accept credit cards and PayPal on this view.
        const accepted_payment_types = {
            'cc_saved': true,
            'cc_new': true,
            'net_terms': false,
            'pay_later': false,
            'paypal': true
        };
        const receipt_callback = () => {
            document.getElementById('js-payments-display-payment-form').addEventListener('click',
                this.display_and_reload_payment_form.bind(this)
            );
        };

        this.bind();

        this.payment_view = new SimplePaymentView(
            accepted_payment_types,
            'js-payments-container',
            true,
            receipt_callback,
            this.logged_in
        );
    }

    /**
     * Adds references to the UI elements and adds event listeners.
     *
     * @private
     * @returns {void}
     */
    bind(): void {
        /**
         * Logged out elements.
         */

        // Add helper to the two reference text inputs to help the cursor jump to the next input automatically.
        this.elements.chain_inputs = document.getElementsByClassName('js-chain-inputs');
        for (let idx = 0; idx < this.elements.chain_inputs.length; idx += 1) {
            const input = this.elements.chain_inputs[idx];
            input.addEventListener('keyup', this.chain_input.bind(this, idx));
        }

        // Add automatic validation to the reference inputs on change.
        this.elements.reference_combo = document.getElementsByClassName('js-reference-combo');
        for (let idx = 0; idx < this.elements.reference_combo.length; idx += 1) {
            const input = this.elements.reference_combo[idx];
            input.addEventListener('change', this.validate_reference.bind(this));
        }

        /**
         * Logged in elements.
         */

        // Add event to fetch the amount on SI or SO drop down change.
        this.elements.so_selector = document.getElementById('submit-payment-so-select');
        if (this.elements.so_selector !== null) {
            this.elements.so_selector.addEventListener('change', this.reference_ddl_change.bind(this, 'so'));
        }

        this.elements.si_selector = document.getElementById('submit-payment-si-select');
        if (this.elements.si_selector !== null) {
            this.elements.si_selector.addEventListener('change', this.reference_ddl_change.bind(this, 'si'));
        }

        // Add references to the logged in selectors.
        this.elements.logged_in_selector = document.getElementById('js-logged-in-selector');
        this.elements.logged_in_selected = document.getElementById('js-logged-in-selected');
        this.elements.logged_in_selected_type = document.getElementById('js-logged-in-selected-ref-type');
        this.elements.logged_in_selected_num = document.getElementById('js-logged-in-selected-ref-num');

        // Add event for the back button. This is used when they click on 'change',
        // but never actually change their reference.
        this.elements.back_to_selected_reference = document.getElementById('js-back-to-selected-reference');

        if (this.elements.logged_in_selector !== null) {
            this.logged_in = true;
        }

        if (this.elements.back_to_selected_reference !== null) {
            this.elements.back_to_selected_reference.addEventListener('click',
                this.toggle_logged_in_selector.bind(this, 'back', '', '')
            );
        }

        // Add event to handle button clicks if they want to change the reference after selecting one.
        const change_ref_button = document.getElementById('js-change-selected-reference');
        if (change_ref_button !== null) {
            change_ref_button.addEventListener('click', this.toggle_logged_in_selector.bind(this, 'change', '', ''));
        }
    }

    /**
     * Helper to move the cursor from one input to the next while the user is typing.
     * Like a serial key. Goes forward and backwards.
     *
     * @event
     * @param {number} idx index of the current input.
     * @param {KeyboardEvent} e event firing calling the function.
     * @returns {void}
     */
    chain_input(idx: number, e: KeyboardEvent): void {
        const element = <HTMLInputElement>e.target;

        // At the end of the current input, go to next input.
        if (element.value.length === element.maxLength) {
            // If we aren't the last input.
            if (this.elements.chain_inputs.length > idx + 1) {
                this.elements.chain_inputs[idx + 1].focus();
            }
            // If pressing backspace and we're at the start of an input, go to the previous input.
        } else if (element.value.length === 0 && e.keyCode === 8) {
            // If we aren't the first input.
            if (idx !== 0) {
                this.elements.chain_inputs[idx - 1].focus();
            }
        }
    }

    /**
     * Make an ajax request to get the balance of the passed reference.
     *
     * @private
     * @param {string} reference_id sales id or invoice id.
     * @returns {void}
     */
    get_balance(reference_id: string): void {
        $.ceAjax('request', 'index.php?dispatch=tv_payments.fetch_balance', {
            'full_render': false,
            'method': 'POST',
            'caching': false,
            'data': {
                'reference_id': reference_id
            },
            'callback': this.get_balance_response.bind(this)
        });
    }

    /**
     * Assigns the result of get_balance to the amount input.
     *
     * @callback
     * @param {Object} data CS Cart ajax response.
     * @returns {void}
     */
    get_balance_response(data): void {
        this.payment_view.elements.amount.value = data.json.balance.toFixed(2);
    }

    /**
     * Shows or hides the drop downs for orders or invoices.
     *
     * @private
     * @param {string} type_of_action hides or displays logged in elements depending on the action.
     * @param {string} type so or si to set text indicator for the user.
     * @param {string} reference_id of the selected reference.
     * @return {void}
     */
    toggle_logged_in_selector(type_of_action: string, type: string, reference_id: string): void {
        if (type_of_action === 'select') {
            this.elements.logged_in_selector.style.display = 'none';

            const display_type = type === 'so' ? 'sales order' : 'invoice';

            this.elements.logged_in_selected_type.innerText = display_type;
            this.elements.logged_in_selected_num.innerText = reference_id;
            this.elements.logged_in_selected.style.display = 'block';
        } else if (type_of_action === 'change') {
            if (this.elements.back_to_selected_reference !== null) {
                this.elements.back_to_selected_reference.style.display = 'inline';
            }

            this.elements.logged_in_selected.style.display = 'none';
            this.elements.logged_in_selector.style.display = 'block';
        } else if (type_of_action === 'back') {
            this.elements.logged_in_selector.style.display = 'none';
            this.elements.logged_in_selected.style.display = 'block';
        }
    }

    /**
     * Handles change events on the reference drop down lists to fetch balance and hide the selector.
     *
     * @event
     * @param {string} type so or si to denote the type.
     * @param {Event} e from the selector that was changed.
     * @returns {void}
     */
    reference_ddl_change(type: string, e: Event): void {
        const element = <HTMLSelectElement>e.target;
        const reference_id = element.options[element.selectedIndex].value;

        if (type === 'so' && this.elements.si_selector !== null) {
            this.elements.si_selector.selectedIndex = 0;
        } else if (type === 'si' && this.elements.so_selector !== null) {
            this.elements.so_selector.selectedIndex = 0;
        }

        this.get_balance(reference_id);

        this.toggle_logged_in_selector('select', type, reference_id);
    }

    /**
     * Display and refresh the form.
     *
     * @callback
     * @returns {void}
     */
    display_and_reload_payment_form(): void {
        // Reload the form with AJAX.
        // Use a call back to reinitialize the view on the new form.
        $.ceAjax('request', 'index.php?dispatch=tv_payments.view', {
            'full_render': false,
            'method': 'POST',
            'caching': false,
            'data': { result_ids: 'js-payments-container' },
            'callback': () => new SubmitPaymentView()
        });
    }

    /**
     * Makes an AJAX request to check that the reference the user filled out is valid.
     *
     * @event
     * @returns {void}
     */
    validate_reference(): void {
        // Don't make the request until we have a complete reference.
        if (this.elements.reference_combo[0].value.length !== 3 ||
            this.elements.reference_combo[1].value.length !== 3
        ) {
            return;
        }

        if (this.elements.reference_combo[2].options[this.elements.reference_combo[2].selectedIndex].value === '') {
            return;
        }

        const data = {
            reference: [
                this.elements.reference_combo[0].value,
                this.elements.reference_combo[1].value,
                this.elements.reference_combo[2].options[this.elements.reference_combo[2].selectedIndex].value
            ]
        };

        $.ceAjax('request', 'index.php?dispatch=tv_payments.validate_reference', {
            'full_render': false,
            'method': 'POST',
            'caching': false,
            'data': data,
            'callback': this.validate_reference_response.bind(this)
        });
    }

    /**
     * Handles the validation ajax request by using the result to display a check box or x with message.
     *
     * @callback
     * @param {Object} data CS Cart ajax response.
     * @returns {void}
     */
    validate_reference_response(data): void {
        if (data.json.is_valid === true) {
            document.getElementById('js-submit-payment-reference-err').classList.add('tv-display__none');
            document.getElementById('js-submit-payment-reference-ok').classList.remove('tv-display__none');
        } else {
            document.getElementById('js-submit-payment-reference-ok').classList.add('tv-display__none');
            document.getElementById('js-submit-payment-reference-err').classList.remove('tv-display__none');
        }
    }
}

/**
 * Class to manage the UI and request logic for a saved credit card.
 *
 * @property {HTMLElement} div containing saved card.
 * @property {Object<string, HTMLElement>} elements references to all of the needed UI elements.
 * @property {number} key index key of saved card within array.
 * @property {boolean} preferred flag for whether this is the preferred card.
 * @property {string} profile_id id for payment profile.
 * @property {boolean} editable flag for whether fields should be editable or not.
 * @property {number} expiration_month variable for card expiration month for comparison.
 * @property {number} expiration_year variable for card expiration year for comparison.
 * @property {string} name_on_card variable for name on card for comparison
 */
class SavedCard {

    div: HTMLElement;
    elements: { [key: string]: any } = {};
    key: number = 0;
    preferred: boolean = false;
    profile_id: string = '';
    editable: boolean = false;
    expiration_month: number = 0;
    expiration_year: number = 0;
    name_on_card: string = '';

    /**
     * @param {HTMLElement} div containing saved card elements.
     */
    constructor(div: HTMLElement = null) {
        this.key = parseInt(div.getAttribute('data-key'), 10);
        this.div = div;
        this.init_from_div();
    }

    /**
     * Pull all control elements from the div.
     *
     * @return {void}
     */
    init_from_div(): void {
        if (!this.div) {
            return;
        }

        this.elements = {
            preferred_btn: this.div.querySelector(`#js-preferred-btn-${this.key}`),
            preferred_txt: this.div.querySelector(`#js-preferred-text-${this.key}`),
            edit_btn: this.div.querySelector(`#js-edit-btn-${this.key}`),
            delete_btn: this.div.querySelector(`#js-delete-btn-${this.key}`),
            expiration_display: {
                no_edit: this.div.querySelector(`#js-expiration-no-edit-${this.key}`),
                edit: this.div.querySelector(`#js-expiration-edit-${this.key}`)
            },
            name_display: {
                no_edit: this.div.querySelector(`#js-name-no-edit-${this.key}`),
                edit: this.div.querySelector(`#js-name-edit-${this.key}`)
            }
        };

        this.profile_id = this.elements.preferred_btn.getAttribute('data-id');
        if (this.elements.preferred_btn.classList.contains('hidden')) {
            this.preferred = true;
        }

        this.expiration_month = this.get_expiration_month();
        this.expiration_year = this.get_expiration_year();
        this.name_on_card = this.get_name_on_card();
    }

    /**
     * Retrieves expiration month from form.
     *
     * @return {number} month as an integer.
     */
    get_expiration_month(): number {
        const month = parseInt(
            this.elements.expiration_display.edit.querySelector(`#js-expiration-month-${this.key}`).value,
            10
        );

        return month;
    }

    /**
     * Retrieves expiration year from form.
     *
     * @return {number} year as an integer.
     */
    get_expiration_year(): number {
        const year = parseInt(
            this.elements.expiration_display.edit.querySelector(`#js-expiration-year-${this.key}`).value,
            10
        );

        return year;
    }

    /**
     * Retrieves name on card from form.
     *
     * @return {string} name on card.
     */
    get_name_on_card(): string {
        return this.elements.name_display.edit.querySelector(`#js-name-on-card-${this.key}`).value;
    }

    /**
     * Makes ajax request to set this card as the primary/preferred card.
     *
     * @return {void}
     */
    set_as_preferred(): void {
        $.ceAjax('request', 'index.php?dispatch=tv_payments.update_primary_card', {
            method: 'POST',
            data: { payment_profile: this.profile_id },
            cache: false,
            callback: this.toggle_preferred_properties.bind(this, true)
        });
    }

    /**
     * Hides or shows preferred tag on div.
     *
     * @param {boolean} preferred flag for whether this is the preferred card.
     * @return {void}
     */
    toggle_preferred_properties(preferred: boolean): void {
        this.preferred = preferred;
        if (this.preferred) {
            this.elements.preferred_btn.classList.add('hidden');
            this.elements.preferred_txt.classList.remove('hidden');

            return;
        }

        this.elements.preferred_btn.classList.remove('hidden');
        this.elements.preferred_txt.classList.add('hidden');
    }

    /**
     * Switches expiration and name elements between display and editable.
     *
     * @param {boolean} editable flag for whether fields should be editable.
     * @return {void}
     */
    toggle_editable(editable: boolean): void {
        this.editable = editable;
        if (this.editable) {
            this.elements.expiration_display.no_edit.classList.add('hidden');
            this.elements.expiration_display.edit.classList.remove('hidden');
            // TODO CSCART 4 - Uncomment to allow name on card to be editable.
            // this.name_display.no_edit.classList.add('hidden');
            // this.name_display.edit.classList.remove('hidden');
            this.elements.edit_btn.innerHTML = 'SAVE';
        } else {
            this.elements.expiration_display.no_edit.classList.remove('hidden');
            this.elements.expiration_display.edit.classList.add('hidden');
            // TODO CSCART 4 - Uncomment to allow name on card to be editable.
            // this.name_display.no_edit.classList.remove('hidden');
            // this.name_display.edit.classList.add('hidden');
            this.elements.edit_btn.innerHTML = 'EDIT';
            this.elements.expiration_display.no_edit.innerHTML = `${this.expiration_month}/${this.expiration_year}`;
        }
    }

    /**
     * Determines if fields are editable.
     *
     * @return {void}
     */
    edit(): void {
        this.editable ? this.save_edits() : this.toggle_editable(true);
    }

    /**
     * Makes ajax request to update card values.
     *
     * @return {void}
     */
    save_edits(): void {
        const name = this.get_name_on_card();
        const year = this.get_expiration_year();
        const month = this.get_expiration_month();

        // Don't send update request if no values have changed.
        if (name === this.name_on_card && year === this.expiration_year && month === this.expiration_month) {
            this.toggle_editable(false);
            return;
        }

        this.name_on_card = name;
        this.expiration_year = year;
        this.expiration_month = month;

        $.ceAjax('request', 'index.php?dispatch=tv_payments.update_card', {
            full_render: false,
            method: 'POST',
            caching: false,
            data: {
                payment_profile: this.profile_id,
                expiration_date: {
                    year: this.expiration_year,
                    month: this.expiration_month
                },
                name: this.name_on_card
            },
            callback: this.toggle_editable.bind(this, false)
        });
    }

    /**
     * Makes ajax request to delete card.
     *
     * @return {void}
     */
    delete(): void {
        $.ceAjax('request', 'index.php?dispatch=tv_payments.delete_card', {
            full_render: false,
            method: 'POST',
            caching: false,
            data: {
                payment_profile: this.profile_id
            },
            callback: this.remove_div.bind(this)
        });
    }

    /**
     * Removes div from DOM.
     *
     * @return {void}
     */
    remove_div(): void {
        this.div.remove();
    }
}

/**
 * Class to manage the UI for the credit card management page.
 *
 * @property {Array} saved_cards to hold all saved cards for an account.
 * @property {Object<string, HTMLElement>} checkbox_elements references to checkbox elements such as 'set preferred'.
 * @property {Object<string, HTMLElement>} add_card_elements references to all
 * of the needed UI elements for adding cards.
 */
export class CreditCardManager {

    saved_cards: Array<SavedCard> = [];
    checkbox_elements: { [key: string]: HTMLElement } = {};
    add_card_elements: { [key: string]: HTMLInputElement } = {};

    constructor() {
        this.init_saved_cards();
        this.bind_add_card();
    }

    /**
     * Creates saved card objects for every saved card element.
     *
     * @return {void}
     */
    init_saved_cards(): void {
        const elements = document.getElementsByClassName('js-saved-cc-card');
        for (const element of elements) {
            const key = parseInt(element.getAttribute('data-key'), 10);
            const card = new SavedCard(<HTMLElement>element);
            this.bind_saved_card(card);
            this.saved_cards[key] = card;
        }
    }

    /**
     * Bind checkbox elements such as preferred.
     * @returns {void}
     */
    bind_checkbox_elements(): void {
        // Set the the preferred checkbox.
        this.checkbox_elements.preferred_cc = document.getElementById('js-credit-card-preferred-container');

        // Check for the preferred cc checkbox and manipulate it.
        if (this.checkbox_elements.preferred_cc) {
            this.checkbox_elements.preferred_cc.classList.add('hidden');

            // If there are saved credit cards, display the preferred checkbox.
            // Filter for non empty values and check the length.
            if (this.saved_cards.filter(() => true).length > 0) {
                this.checkbox_elements.preferred_cc.classList.remove('hidden');
            }
        }
    }

    /**
     * Binds events for saved card.
     *
     * @param {SavedCard} card instance of SavedCard class.
     * @return {void}
     */
    bind_saved_card(card: SavedCard): void {
        card.elements.preferred_btn.addEventListener('click', this.change_preferred_card.bind(this));
        card.elements.delete_btn.addEventListener('click', this.delete_card.bind(this));
        card.elements.edit_btn.addEventListener('click', this.edit_card.bind(this));
    }

    /**
     * Assigns UI control elements for add credit card form and binds events.
     *
     * @return {void}
     */
    bind_add_card(): void {
        this.add_card_elements = {
            'credit_card_number': <HTMLInputElement>document.getElementById('credit_card_number_1'),
            'credit_card_month': <HTMLInputElement>document.getElementById('credit_card_month_1'),
            'credit_card_year': <HTMLInputElement>document.getElementById('credit_card_year_1'),
            'credit_card_cvv2': <HTMLInputElement>document.getElementById('credit_card_cvv2_1'),
            'credit_card_name': <HTMLInputElement>document.getElementById('credit_card_name_1'),
            'preferred_checkbox': <HTMLInputElement>document.getElementById('credit_card_preferred1'),
            'add_cc_button': <HTMLInputElement>document.getElementById('js-select-payment-button')
        };

        this.add_card_elements.add_cc_button.addEventListener('click', this.add_card.bind(this));

        document.getElementById('credit_card_save1_group').classList.add('hidden');
        (<HTMLInputElement>document.getElementById('credit_card_save1')).checked = true;
    }

    /**
     * Handles the saved cards' click event on the set as preferred buttons.
     *
     * @event
     * @param {Event} e from the set as preferred button.
     * @return {void}
     */
    change_preferred_card(e: Event): void {
        const element = <HTMLElement>e.target;
        const key = parseInt(element.getAttribute('data-key'), 10);

        for (const card of this.saved_cards) {
            // Any card that is set as preferred we want toggle it to not preferred.
            if (card.preferred) {
                card.toggle_preferred_properties(false);
            }
            if (card.key === key) {
                card.set_as_preferred();
            }
        }
    }

    /**
     * Handles the saved cards' click event on the delete button.
     *
     * @event
     * @param {Event} e from the delete card button.
     * @return {void}
     */
    delete_card(e: Event): void {
        const element = <HTMLElement>e.target;
        const key = parseInt(element.getAttribute('data-key'), 10);

        // Delete the card from Authorize.
        this.saved_cards[key].delete();

        // Delete the card from the saved cards array. We use the 'delete' operator here because
        // we don't want the indexes to reset when removing a card.
        delete this.saved_cards[key];

        // Rebind the checkboxes to set the display for the preferred checkbox.
        this.bind_checkbox_elements();
    }

    /**
     * Handles the saved cards' click event on the edit button.
     *
     * @event
     * @param {Event} e from the edit card button.
     * @return {void}
     */
    edit_card(e: Event): void {
        const element = <HTMLElement>e.target;
        const key = parseInt(element.getAttribute('data-key'), 10);

        this.saved_cards[key].edit();
    }

    /**
     * Handles click event for the add card button.
     *
     * @event
     * @param {Event} e from the add card button.
     * @return {void}
     */
    add_card(e: Event): void {
        // Stop HTML from posting the form.
        e.preventDefault();
        e.stopPropagation();

        // Create Accept.js request.
        const acceptjs_request = new AcceptJSRequest(
            (<HTMLInputElement>document.getElementById('js-accept-js-client-key')).value,
            (<HTMLInputElement>document.getElementById('js-accept-js-login-id')).value
        );
        acceptjs_request.set_card_data(
            this.add_card_elements.credit_card_name.value,
            this.add_card_elements.credit_card_number.value.replace(/\s+/g, ''),
            this.add_card_elements.credit_card_month.value,
            this.add_card_elements.credit_card_year.value,
            this.add_card_elements.credit_card_cvv2.value
        );
        // TODO : private method.
        acceptjs_request.send_request(this.finalize_add_card.bind(this));
    }

    /**
     * Checks for errors from the accept.js request response. Then pulls in all of
     * the credit card data from the form and makes a request to add the card.
     *
     * @callback
     * @param {Object} response from accept.js
     * @return {void}
     */
    finalize_add_card(response): void {
        if (response.error) {
            Notifications.set_notification('E', 'Failed to add credit card.', response.error_msgs.join(' '));
            return;
        }

        (<HTMLInputElement>document.getElementById('js-accept-js-data-value')).value = response.data_value;
        (<HTMLInputElement>document.getElementById('js-accept-js-data-descriptor')).value = response.data_descriptor;

        const num_without_spaces = this.add_card_elements.credit_card_number.value.replace(/\s+/g, '');
        (<HTMLInputElement>document.getElementById('js-payments-last-four')).value = num_without_spaces.slice(-4);

        const form_data = $('#js-add-credit-card-form').serializeArray();

        // CS Cart doesn't like the jQuery serialization so we have to reassign the objects to key value pairs.
        const data = {};
        for (const property of form_data) {
            data[property.name] = property.value;
        }
        data['result_ids'] = 'js-manage-saved-cards';

        $.ceAjax('request', 'index.php?dispatch=tv_payments.add_card', {
            method: 'POST',
            cache: false,
            data: data,
            result_ids: 'js-manage-saved-cards',
            callback: this.card_added.bind(this)
        });
    }

    /**
     * Checks to see if response from controller was an error and if not it clears the add
     * cc form and re-initializes all of the saved cards.
     *
     * @callback
     * @param {Objects} response from frontend controller.
     * @return {void}
     */
    card_added(response): void {
        if (response.notification[0] === 'E') {
            return;
        }

        this.reset_add_cc_form();
        this.saved_cards = [];
        this.init_saved_cards();
        this.bind_checkbox_elements();
    }

    /**
     * Clears all of the add cc form fields.
     *
     * @return {void}
     */
    reset_add_cc_form(): void {
        this.add_card_elements.credit_card_number.value = '';
        this.add_card_elements.credit_card_month.value = '';
        this.add_card_elements.credit_card_year.value = '';
        this.add_card_elements.credit_card_cvv2.value = '';
        this.add_card_elements.credit_card_name.value = '';
        // If there aren't saved cards, the checkbox won't be there.
        if (this.add_card_elements.preferred_checkbox) {
            this.add_card_elements.preferred_checkbox.checked = false;
        }
        (<HTMLInputElement>document.getElementById('js-card-type')).value = '';
    }
}

/**
 * Looks for the native place_order button, and if it exists, initializes an instance of the CheckoutView.
 *
 * @returns {void}
 */
export function init() {
    if (document.getElementById('js-submit-payment-form')) {
        (() => new SubmitPaymentView())();
    }

    if (document.getElementById('js-manage-saved-cards')) {
        (() => new CreditCardManager())();
    }
}
